import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
export type LegalDoc = 'privacy' | 'terms' | null;
interface LegalModalProps {
  doc: LegalDoc;
  onClose: () => void;
}
export function LegalModal({
  doc,
  onClose
}: LegalModalProps) {
  useEffect(() => {
    if (doc) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [doc]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return <AnimatePresence data-id="element-171">
      {doc && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8" aria-modal="true" role="dialog" data-id="element-172">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-plum-950/80 backdrop-blur-sm" onClick={onClose} data-id="element-173" />

          {/* Panel */}
          <motion.div initial={{
        opacity: 0,
        y: 30,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 30,
        scale: 0.98
      }} transition={{
        duration: 0.3
      }} className="relative w-full max-w-3xl max-h-[88vh] flex flex-col bg-gradient-to-br from-plum-900 to-plum-950 border border-lavender-400/20 rounded-2xl shadow-2xl shadow-plum-950/60 overflow-hidden" data-id="element-174">
            {/* Header */}
            <div className="flex items-center justify-between px-6 md:px-10 py-5 border-b border-lavender-400/10 flex-shrink-0" data-id="element-175">
              <h2 className="font-serif text-2xl md:text-3xl text-lavender-50" data-id="element-176">
                {doc === 'privacy' ? 'Massage Therapy Policies' : 'Terms of Service'}
              </h2>
              <button onClick={onClose} aria-label="Close" className="text-lavender-200/60 hover:text-rose-300 transition-colors p-2 -mr-2" data-id="element-177">
                <X size={24} data-id="element-178" />
              </button>
            </div>

            {/* Scrollable content */}
            <div className="overflow-y-auto px-6 md:px-10 py-8 text-lavender-100/75 font-light leading-relaxed space-y-8" data-id="element-179">
              {doc === 'privacy' ? <PrivacyContent data-id="element-180" /> : <TermsContent data-id="element-181" />}
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}
function H({
  children
}: {
  children: React.ReactNode;
}) {
  return <h3 className="font-serif text-xl md:text-2xl text-lavender-50 mb-3" data-id="element-182">
      {children}
    </h3>;
}
function P({
  children
}: {
  children: React.ReactNode;
}) {
  return <p className="text-sm md:text-[15px] mb-3" data-id="element-183">{children}</p>;
}
function List({
  items
}: {
  items: string[];
}) {
  return <ul className="space-y-2 mb-3 pl-1" data-id="element-184">
      {items.map((item, i) => <li key={i} className="flex gap-3 text-sm md:text-[15px]" data-id="element-185">
          <span className="text-rose-400 mt-1.5 flex-shrink-0 w-1 h-1 rounded-full bg-rose-400" data-id="element-186" />
          <span data-id="element-187">{item}</span>
        </li>)}
    </ul>;
}
function PrivacyContent() {
  return <>
      <section data-id="element-188">
        <H data-id="element-189">Appointment Policy</H>
        <P data-id="element-190">
          Appointments are reserved specifically for you. Please arrive on time
          to receive the full length of your scheduled session. Late arrivals
          may result in a shortened session to avoid impacting other scheduled
          appointments.
        </P>
      </section>

      <section data-id="element-191">
        <H data-id="element-192">Cancellation &amp; Rescheduling Policy</H>
        <P data-id="element-193">
          We kindly request at least 24 hours' notice for any cancellations or
          appointment changes.
        </P>
        <List items={['Cancellations made with less than 24 hours\u2019 notice may be subject to a cancellation fee.', 'No-shows may be charged up to 100% of the scheduled service cost.', 'Repeated last-minute cancellations may require prepayment for future appointments.']} data-id="element-194" />
      </section>

      <section data-id="element-195">
        <H data-id="element-196">Payment Policy</H>
        <P data-id="element-197">
          Payment is due at the time services are rendered unless otherwise
          agreed upon.
        </P>
        <P data-id="element-198">We accept:</P>
        <List items={['Cash', 'Credit/Debit Cards', 'Electronic Payments']} data-id="element-199" />
        <P data-id="element-200">Prices are subject to change without notice.</P>
      </section>

      <section data-id="element-201">
        <H data-id="element-202">Health &amp; Medical Information</H>
        <P data-id="element-203">
          Clients are responsible for providing accurate and complete health
          information prior to treatment.
        </P>
        <P data-id="element-204">Please inform your therapist of:</P>
        <List items={['Injuries', 'Medical conditions', 'Recent surgeries', 'Pregnancy', 'Allergies', 'Medications that may affect treatment']} data-id="element-205" />
        <P data-id="element-206">
          Massage therapy is not a substitute for medical care, diagnosis, or
          treatment by a licensed healthcare provider.
        </P>
      </section>

      <section data-id="element-207">
        <H data-id="element-208">Professional Conduct Policy</H>
        <P data-id="element-209">
          Massage therapy is provided strictly for therapeutic and relaxation
          purposes.
        </P>
        <List items={['Any inappropriate behavior, comments, or requests will result in immediate termination of the session.', 'Full payment will still be required for terminated sessions.', 'The therapist reserves the right to refuse service to anyone for inappropriate conduct, intoxication, or safety concerns.']} data-id="element-210" />
      </section>

      <section data-id="element-211">
        <H data-id="element-212">Hygiene Policy</H>
        <P data-id="element-213">For the comfort and safety of all clients:</P>
        <List items={['Please arrive clean and free from excessive fragrances.', 'Clients experiencing contagious illnesses, fever, flu-like symptoms, or skin infections should reschedule their appointment.', 'Fresh linens and sanitized equipment are used between every client.']} data-id="element-214" />
      </section>

      <section data-id="element-215">
        <H data-id="element-216">Pregnancy Massage Policy</H>
        <P data-id="element-217">
          Clients who are pregnant should notify the therapist when booking an
          appointment. Certain conditions may require written approval from a
          healthcare provider before treatment.
        </P>
      </section>

      <section data-id="element-218">
        <H data-id="element-219">Personal Belongings</H>
        <P data-id="element-220">
          Please keep personal belongings with you at all times. We are not
          responsible for lost, stolen, or damaged items.
        </P>
      </section>

      <section data-id="element-221">
        <H data-id="element-222">Results Disclaimer</H>
        <P data-id="element-223">
          Massage therapy benefits vary by individual. No specific results can
          be guaranteed. Regular sessions, proper self-care, and following
          professional recommendations may help maximize benefits.
        </P>
      </section>

      <section data-id="element-224">
        <H data-id="element-225">Right to Refuse Service</H>
        <P data-id="element-226">
          We reserve the right to refuse or discontinue services for any reason
          that may compromise the safety, comfort, health, or well-being of the
          therapist or client.
        </P>
      </section>

      <section data-id="element-227">
        <H data-id="element-228">Agreement</H>
        <P data-id="element-229">
          By scheduling and receiving services, you acknowledge that you have
          read, understood, and agreed to these policies and terms of service.
        </P>
      </section>
    </>;
}
function TermsContent() {
  return <>
      <P data-id="element-230">
        <span className="text-lavender-200/60" data-id="element-231">
          Effective Date: [Insert Date]
        </span>
      </P>
      <P data-id="element-232">
        Welcome to Afriels Spa ("we," "our," or "us"). By accessing our website,
        booking an appointment, or receiving services, you agree to be bound by
        the following Terms of Service.
      </P>

      <section data-id="element-233">
        <H data-id="element-234">1. Services</H>
        <P data-id="element-235">
          We provide professional massage therapy and wellness services. All
          services are intended to promote relaxation, stress reduction, and
          overall well-being and are not intended to diagnose, treat, cure, or
          prevent any medical condition.
        </P>
      </section>

      <section data-id="element-236">
        <H data-id="element-237">2. Eligibility</H>
        <P data-id="element-238">
          By booking an appointment, you represent that you are at least 18
          years of age or have the consent of a parent or legal guardian.
        </P>
      </section>

      <section data-id="element-239">
        <H data-id="element-240">3. Appointment Scheduling</H>
        <P data-id="element-241">
          Appointments may be scheduled online, by phone, text message, or
          through other approved booking methods. Appointment availability is
          subject to change without notice.
        </P>
      </section>

      <section data-id="element-242">
        <H data-id="element-243">4. Cancellation and No-Show Policy</H>
        <P data-id="element-244">
          Clients are required to provide at least 24 hours' notice for
          cancellations or rescheduling. Failure to provide adequate notice may
          result in:
        </P>
        <List items={['A cancellation fee', 'Loss of deposit', 'Full charge for the scheduled service', 'Restrictions on future bookings']} data-id="element-245" />
      </section>

      <section data-id="element-246">
        <H data-id="element-247">5. Payments</H>
        <P data-id="element-248">
          Payment is due at the time services are rendered unless otherwise
          stated. We reserve the right to:
        </P>
        <List items={['Modify pricing at any time', 'Require deposits for certain appointments', 'Refuse service for unpaid balances']} data-id="element-249" />
      </section>

      <section data-id="element-250">
        <H data-id="element-251">6. Health Disclosure</H>
        <P data-id="element-252">
          Clients agree to provide accurate and complete health information that
          may affect massage treatment. You agree to notify your therapist of:
        </P>
        <List items={['Medical conditions', 'Injuries', 'Pregnancy', 'Recent surgeries', 'Allergies', 'Medications']} data-id="element-253" />
        <P data-id="element-254">
          Failure to disclose relevant health information may affect the safety
          and effectiveness of services.
        </P>
      </section>

      <section data-id="element-255">
        <H data-id="element-256">7. Medical Disclaimer</H>
        <P data-id="element-257">
          Massage therapy services provided by the Company are not a substitute
          for medical diagnosis, treatment, or professional healthcare advice.
          Clients should consult a licensed physician regarding any medical
          concerns.
        </P>
      </section>

      <section data-id="element-258">
        <H data-id="element-259">8. Professional Boundaries</H>
        <P data-id="element-260">
          All services are strictly professional and therapeutic in nature. Any
          inappropriate conduct, sexual remarks, advances, harassment, or
          requests for services outside the therapist's professional scope will
          result in immediate termination of the session and may result in
          refusal of future services.
        </P>
        <P data-id="element-261">
          No refunds will be provided for sessions terminated due to
          inappropriate behavior.
        </P>
      </section>

      <section data-id="element-262">
        <H data-id="element-263">9. Refusal of Service</H>
        <P data-id="element-264">
          We reserve the right to refuse or discontinue service to any
          individual for reasons including but not limited to:
        </P>
        <List items={['Safety concerns', 'Inappropriate behavior', 'Intoxication', 'Failure to comply with policies', 'Health conditions that may contraindicate treatment']} data-id="element-265" />
      </section>

      <section data-id="element-266">
        <H data-id="element-267">10. Limitation of Liability</H>
        <P data-id="element-268">
          To the fullest extent permitted by law, the Company shall not be
          liable for any indirect, incidental, special, or consequential damages
          arising from the use of our website or services. Clients voluntarily
          participate in massage therapy services and assume any associated
          risks.
        </P>
      </section>

      <section data-id="element-269">
        <H data-id="element-270">11. Website Use</H>
        <P data-id="element-271">Users agree not to:</P>
        <List items={['Use the website for unlawful purposes', 'Attempt unauthorized access to website systems', 'Upload harmful software or malicious code', 'Interfere with website functionality']} data-id="element-272" />
        <P data-id="element-273">
          We reserve the right to suspend or terminate access to users who
          violate these terms.
        </P>
      </section>

      <section data-id="element-274">
        <H data-id="element-275">12. Intellectual Property</H>
        <P data-id="element-276">
          All website content, including text, graphics, logos, images, and
          branding materials, is the property of the Company and may not be
          copied, reproduced, or distributed without written permission.
        </P>
      </section>

      <section data-id="element-277">
        <H data-id="element-278">13. Privacy</H>
        <P data-id="element-279">
          Your use of our website is also governed by our Privacy Policy. By
          using our services, you consent to the collection and use of
          information as described in that policy.
        </P>
      </section>

      <section data-id="element-280">
        <H data-id="element-281">14. Changes to These Terms</H>
        <P data-id="element-282">
          We reserve the right to update or modify these Terms of Service at any
          time. Changes become effective immediately upon posting to our
          website.
        </P>
      </section>

      <section data-id="element-283">
        <H data-id="element-284">15. Governing Law</H>
        <P data-id="element-285">
          These Terms of Service shall be governed and interpreted under the
          laws of the State of [Your State], without regard to conflict of law
          principles.
        </P>
      </section>

      <section data-id="element-286">
        <H data-id="element-287">16. Contact Information</H>
        <P data-id="element-288">
          If you have any questions regarding these Terms of Service, please
          contact:
        </P>
        <P data-id="element-289">
          Website URL: <span className="text-rose-300" data-id="element-290">www.afriels.com</span>
        </P>
      </section>

      <P data-id="element-291">
        By using this website, booking an appointment, or receiving services,
        you acknowledge that you have read, understood, and agree to be bound by
        these Terms of Service.
      </P>
    </>;
}